import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { formatDate, DATE_FORMATS } from '@wix/da-i18next/pkg/time/functions';
import { NotificationQueueItem, Notification } from '@wix/da-shared-react/pkg/Notifications';
import { NotificationDuration } from '@wix/da-shared-react/pkg/Notifications/Notification';
import SystemNotification from '@wix/da-shared-react/pkg/SystemNotification';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import { scheduleDraftPublish } from '@wix/da-submit-shared/pkg/redux/actions';
import { useEditDeviationClick } from '../../hooks/useEditDeviationClick';

import s from './DeviationScheduleNotification.scss';

export interface Props extends Omit<NotificationQueueItem, 'type'> {
  deviation: PapiDeviation;
  scheduleTs: number;
}

const DeviationScheduleNotification: FC<Props> = ({ id, queueName, deviation, scheduleTs }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { onEditClick } = useEditDeviationClick(deviation);
  const onDisableClick = useCallback(() => {
    dispatch(
      scheduleDraftPublish({
        deviation,
        scheduleTs: 0,
      })
    );
  }, [dispatch, deviation]);

  const icon = <Dumbo deviation={deviation} width={48} height={48} />;

  return (
    <Notification
      id={id}
      queueName={queueName}
      cardComponent={SystemNotification}
      cardProps={{
        className: s['notification-card'],
        textClassName: s['notification-card-text'],
        iconClassName: s['notification-card-icon'],
        customIcon: icon,
        multiLine: true,
        limitTextWidth: false,
        duration: NotificationDuration.S,
        actions: [
          {
            label: t('common.disable'),
            onClick: onDisableClick,
            closeOnClick: true,
          },
          {
            label: t('common.edit'),
            onClick: onEditClick,
            closeOnClick: true,
          },
        ],
      }}
    >
      {t('studio.notifications.deviationSchedule.text', {
        title: deviation.title,
        date: formatDate(scheduleTs, DATE_FORMATS.DateTime.Full, i18n.language),
      })}
    </Notification>
  );
};
DeviationScheduleNotification.displayName = 'DeviationScheduleNotification';

export default DeviationScheduleNotification;
